(() => {
  'use strict';
  if (customElements.get('ts-collection-results')) return;
  const collectionViews = new Map();
  const normalize = value => String(value || '').normalize('NFKD').toLowerCase().replace(/×|\bby\b/g, 'x').replace(/(\d)\s*x\s*(?=\d)/g, '$1 ').replace(/[^a-z0-9]+/g, ' ').trim();
  const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const numeric = (a, b) => String(a).localeCompare(String(b), undefined, {numeric: true});
  const facetAliases = {
    'Size':['size'], 'Depth':['depth','boxdepth','vaultdepth'], 'Width':['width'], 'Height':['height'], 'Dimensions':['dimensions'],
    'Manufacturer':['manufacturer','vendor','brand'], 'Product type':['producttype','type'], 'Availability':['availability','stock'],
    'Load rating':['loadrating','tierrating','tier','rating'], 'Material':['material'], 'Lid type':['lidtype','covertype','lid'],
    'Fiber mode':['fibermode','mode','fibretype','fibertype'], 'Fiber count':['fibercount','fibrecount','strandcount','fiberquantity'],
    'Application':['application','installation','environment'], 'Jacket rating':['jacketrating','flamerating'], 'Jacket':['jacket'],
    'Construction':['construction','armored','armoured'], 'Cable type':['cabletype'], 'Category':['category','cablecategory'],
    'Gauge':['gauge','awg','wiregauge'], 'Shielding':['shielding','shielded'],
    'Rack height':['rackheight','rackunits','rackunit','usize','ru'], 'Style':['style'], 'Mounting':['mounting','mountingtype'],
    'Tool type':['tooltype'], 'Connector type':['connectortype','connector','connectors'], 'Connector A':['connectora','connector1','end1','enda'],
    'Connector B':['connectorb','connector2','end2','endb'], 'Polish':['polish','polishtype'], 'Color':['color','colour'],
    'Port count':['portcount','ports','numberofports'], 'Speed':['speed','datarate'], 'PoE':['poe','poweroverethernet']
  };
  const facetKey = label => {
    const key = String(label || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return Object.keys(facetAliases).find(name => facetAliases[name].includes(key)) || '';
  };
  const facetValues = (key, value) => {
    const flatten = input => Array.isArray(input) ? input.flatMap(flatten) : input && typeof input === 'object' ? 'unit' in input ? [`${input.value} ${input.unit}`] : flatten(input.name ?? input.label ?? input.value ?? '') : input == null ? [] : [input];
    const clean = raw => {
      if (key === 'Construction' && typeof raw === 'boolean') return raw ? 'Armored' : 'Non-armored';
      let text = String(raw).replace(/<[^>]+>/g, '').trim(); if (!text || text === 'Default Title') return '';
      const low = text.toLowerCase();
      if (key === 'Fiber mode') {
        if (/single[ -]?mode|\bos[12]\b/.test(low)) return 'Singlemode';
        const om = text.match(/\bom[1-5]\b/i); if (om) return om[0].toUpperCase();
      }
      if (key === 'Fiber count') { const n = text.match(/^\s*(\d+)\s*(?:fiber|fibre|strand)?s?\s*$/i); if (n) return `${n[1]} Fiber`; }
      if (key === 'Application') {
        if (/^indoor\s*(?:\/|&|and|-)?\s*outdoor$/i.test(text)) return 'Indoor/Outdoor';
        if (/^indoor$/i.test(text)) return 'Indoor'; if (/^outdoor$/i.test(text)) return 'Outdoor'; if (/^direct[ -]?burial$/i.test(text)) return 'Direct burial';
      }
      if (key === 'Construction') {
        if (/^(?:non[ -]?armou?red|unarmou?red|no|false)$/i.test(text)) return 'Non-armored';
        if (/^micro[ -]?armou?red$/i.test(text)) return 'Micro-armored';
        if (/^(?:armou?red|yes|true)$/i.test(text)) return 'Armored';
      }
      if (key === 'Jacket rating') { if (/plenum|\bcmp\b/.test(low)) return 'Plenum'; if (/riser|\bcmr\b/.test(low)) return 'Riser'; if (/lszh/.test(low)) return 'LSZH'; }
      if (key === 'Load rating' && /^\s*(?:tier\s*)?(15|22)\s*$/i.test(text)) return 'Tier '+text.match(/15|22/)[0];
      if (key === 'Rack height' && /^\d+\s*(?:u|ru)?$/i.test(text)) return text.match(/\d+/)[0]+'U';
      if (['Depth','Width','Height'].includes(key)) text = text.replace(/\s*(?:inches|inch|in\.?|["″])$/i, ' in.');
      if (key === 'Size') text = text.replace(/\s*[x×]\s*/gi, ' × ').replace(/["″]/g,'').replace(/\s+(?:inches|inch|in\.)$/i,'');
      return text;
    };
    return [...new Set(flatten(value).map(clean).filter(Boolean))];
  };
  const bulkNumber = '(?:\\d+[ -]+\\d+\\/\\d+|\\d+\\/\\d+|\\d+(?:\\.\\d+)?)';
  const bulkUnit = '(?:inches|inch|in\\.?|["″]|feet|foot|ft\\.?|[\'′]|mm|cm|m)';
  const bulkDimension = bulkNumber + '\\s*' + bulkUnit + '?';
  const bulkDimensions = new RegExp(bulkDimension + '(?:\\s*(?:x|×|by)\\s*' + bulkDimension + ')+', 'i');
  const bulkMeasure = new RegExp('(' + bulkNumber + ')\\s*(' + bulkUnit + ')?', 'gi');
  const bulkSingleMeasure = new RegExp(bulkNumber + '\\s*' + bulkUnit + '(?=\\s|$|[),])', 'i');
  const bulkFractions = {'¼':'1/4','½':'1/2','¾':'3/4','⅛':'1/8','⅜':'3/8','⅝':'5/8','⅞':'7/8','⅓':'1/3','⅔':'2/3'};
  const bulkPhrase = value => normalize(value).replace(/\b([a-z]{3,})s\b/g, '$1');
  const containsPhrase = (text, phrase) => phrase && (' ' + text + ' ').includes(' ' + phrase + ' ');

  function bulkSize(title) {
    const text = String(title).replace(/[¼½¾⅛⅜⅝⅞⅓⅔]/g, value => ' ' + bulkFractions[value]).replace(/(\d)⁄(\d)/g, '$1/$2');
    const size = text.match(bulkDimensions)?.[0] || text.match(bulkSingleMeasure)?.[0] || '';
    const parts = Array.from(size.matchAll(bulkMeasure));
    const fallbackUnit = parts.find(part => part[2])?.[2] || 'in';
    const dimensions = parts.map(([, raw, givenUnit]) => {
      const numbers = raw.split(/[ -]+/);
      const last = numbers.pop();
      let value = Number(last);
      if (last.includes('/')) {
        const [numerator, denominator] = last.split('/').map(Number);
        value = denominator > 0 ? numerator / denominator + Number(numbers[0] || 0) : Infinity;
      }
      const unit = (givenUnit || fallbackUnit).toLowerCase().replace('.', '');
      const factors = {mm: 1 / 25.4, cm: 1 / 2.54, m: 1000 / 25.4, ft: 12, feet: 12, foot: 12, "'": 12, '′': 12};
      return value * (factors[unit] || 1);
    });
    // Bolt titles list diameter first and length second. Length controls their display order.
    if (/\bbolts?\b/i.test(text) && dimensions.length === 2) dimensions.reverse();
    return {dimensions, family: bulkPhrase(text.replace(size, '').replace(/\s*(?:[-–—]\s*)?\bbulk\b.*$/i, '').replace(/\s+with\b.*$|\s+w\/.*$|\s*\(.*$/i, ''))};
  }

  class CollectionResults extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      if (this.dataset.bulkRedirect) {
        const target = new URL(this.dataset.bulkRedirect, location.href);
        const current = new URL(location.href);
        for (const key of ['preview_theme_id', 'tsc_filters', 'tsc_q', 'tsc_sort', 'tsc_view']) {
          if (current.searchParams.has(key)) target.searchParams.set(key, current.searchParams.get(key));
        }
        location.replace(target.href);
        return;
      }
      this.initialized = true;
      this.abort = new AbortController();
      this.grid = this.querySelector('#product-grid');
      this.search = this.querySelector('[data-collection-search]');
      this.status = this.querySelector('[data-status]');
      this.selected = new Set();
      this.records = [];
      this.filters = {};
      this.nativeFilters = [];
      try { this.nativeFilters = JSON.parse(this.querySelector('[data-native-filters]')?.textContent || '[]'); } catch (_) {}
      this.excludedNativeFilters = this.nativeFilters.filter(filter => filter.excluded);
      this.nativeFilters = this.nativeFilters.filter(filter => !filter.excluded);
      this.bulkCategories = this.querySelector('[data-bulk-categories]');
      if (this.bulkCategories) {
        this.bulkNames = Array.from(this.bulkCategories.querySelectorAll('[data-bulk-category]'), button => button.dataset.bulkCategory).filter(Boolean);
        const seen = new Set();
        this.bulkMenu = [];
        document.querySelectorAll('#telecom-reusable-header a[data-nav-path][href*="/collections/"]').forEach(link => {
          const parts = link.dataset.navPath.split(' / ');
          const handle = link.pathname.match(/\/collections\/([^/]+)\/?$/)?.[1];
          if (parts.length < 2 || !handle || !this.bulkNames.includes(parts[0])) return;
          const key = parts[0] + '/' + handle;
          if (seen.has(key)) return;
          seen.add(key);
          const label = parts.at(-1);
          this.bulkMenu.push({
            department: parts[0], handle, index: this.bulkMenu.length,
            path: bulkPhrase(link.dataset.navPath), handleWords: bulkPhrase(handle),
            names: [handle, label, ...label.split(/\/|&/)].map(bulkPhrase).filter(Boolean),
            groups: parts.slice(1, -1).map(bulkPhrase)
          });
        });
      }
      const params = new URLSearchParams(location.search);
      // Always capture product positions in this collection's live Shopify default order.
      // A customer may still choose a local price/name sort without replacing that baseline.
      const nativeSorts = {'price-ascending':'price-asc','price-descending':'price-desc','title-ascending':'name'};
      const requestedSort = params.get('tsc_sort') || (params.get('sort_by') !== this.dataset.defaultSort ? nativeSorts[params.get('sort_by')] : '') || 'featured';
      if (['featured','price-asc','price-desc','name'].includes(requestedSort)) this.querySelector('[data-sort]').value = requestedSort;
      const recommendedURL = this.recommendedURL(new URL(location.href));
      const changingBaseline = this.dataset.defaultSort && this.dataset.currentSort && this.dataset.currentSort !== this.dataset.defaultSort;
      if (changingBaseline) {
        recommendedURL.searchParams.delete('page');
        if (requestedSort !== 'featured') recommendedURL.searchParams.set('tsc_sort', requestedSort);
        else recommendedURL.searchParams.delete('tsc_sort');
      }
      if (recommendedURL.search !== location.search) { location.replace(recommendedURL.href); return; }
      this.search.value = params.get('tsc_q') || '';
      try {
        const filters = JSON.parse(params.get('tsc_filters') || '{}');
        for (const [key, values] of Object.entries(filters)) {
          if (!Array.isArray(values)) continue;
          if (key === 'Department' && this.bulkNames?.includes(values[0])) this.filters.Department = [values[0]];
          else if (facetKey(key)) this.filters[facetKey(key)] = facetValues(facetKey(key), values);
        }
      } catch (_) {}
      this.page = Number(this.dataset.page);
      this.pageSize = Number(this.dataset.pageSize);
      this.pages = Math.max(1, Number(this.dataset.pages));
      this.param = this.dataset.pageParam || 'page';
      this.loaded = new Set([this.page]);
      this.pending = new Map();
      this.preparedPage = null;
      this.addEventListener('ts:prepare-page', event => this.preparePage(event.target), {signal: this.abort.signal});
      this.failed = false;
      this.indexWanted = this.indexing = false;
      this.addRecords(this.grid.querySelectorAll('.product-card'), this.page);
      this.viewKey = `${location.pathname}:${this.dataset.sectionId}`;
      this.setView(params.get('tsc_view') === 'list' ? 'list' : collectionViews.get(this.viewKey) || 'grid');
      this.addEventListener('click', event => {
        const link = event.target.closest('a[data-page-step], a[data-page-number]');
        if (link && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          if (link.getAttribute('aria-disabled') === 'true') return;
          const target = link.hasAttribute('data-page-number') ? Number(link.dataset.pageNumber) : this.page + Number(link.dataset.pageStep);
          this.movePage(target);
          return;
        }
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-bulk-category')) {
          this.filters.Department = button.dataset.bulkCategory ? [button.dataset.bulkCategory] : [];
          this.writeLocalState();
          return;
        }
        if (button.hasAttribute('data-reset')) { this.filters = {}; this.search.value = ''; if (this.hasNativeSelection()) this.goNative(this.dataset.collectionUrl || location.pathname); else this.writeLocalState(); }
        if (button.hasAttribute('data-clear-search')) { this.search.value = ''; this.writeLocalState(); this.search.focus(); }
        if (button.hasAttribute('data-remove-key')) { this.filters[button.dataset.removeKey] = (this.filters[button.dataset.removeKey] || []).filter(value => value !== button.dataset.removeValue); this.writeLocalState(); }
        if (button.hasAttribute('data-filter-toggle')) {
          const open = button.getAttribute('aria-expanded') !== 'true';
          this.toggleFilters(open);
          if (open) this.loadIndex();
        }
        if (button.hasAttribute('data-apply-filters')) this.toggleFilters(false);
        if (button.hasAttribute('data-native-url')) this.goNative(button.dataset.nativeUrl);
        if (button.dataset.view) { this.setView(button.dataset.view); this.writeLocalState(); }
        if (button.hasAttribute('data-retry')) this.refresh();
        if (button.hasAttribute('data-clear-compare')) { this.selected.clear(); this.updateCompare(); }
        if (button.hasAttribute('data-open-compare')) this.openComparison();
        if (button.hasAttribute('data-close-compare')) this.querySelector('dialog').close();
      }, { signal: this.abort.signal });
      this.addEventListener('change', event => {
        const native = event.target.closest('[data-native-filter]');
        if (native) { this.goNative(native.checked ? native.dataset.addUrl : native.dataset.removeUrl); return; }
        const facet = event.target.closest('[data-facet]');
        if (facet) { const values = new Set(this.filters[facet.dataset.facet] || []); if (facet.checked) values.add(facet.value); else values.delete(facet.value); this.filters[facet.dataset.facet] = [...values]; this.writeLocalState(); }
        if (event.target.matches('[data-sort]')) this.writeLocalState();
        const box = event.target.closest('[data-compare]');
        if (!box) return;
        if (box.checked && this.selected.size < 3) this.selected.add(box.dataset.compare);
        else this.selected.delete(box.dataset.compare);
        this.updateCompare();
      }, { signal: this.abort.signal });
      this.search.addEventListener('input', () => this.writeLocalState(), { signal: this.abort.signal });
      this.addEventListener('submit', event => {
        const form = event.target.closest('[data-native-price]'); if (!form) return;
        event.preventDefault(); const url = new URL(location.href);
        form.querySelectorAll('input[name]').forEach(input => { if (input.value === '') url.searchParams.delete(input.name); else url.searchParams.set(input.name, input.value); });
        this.goNative(url.href);
      }, { signal: this.abort.signal });
      this.addEventListener('error', event => {
        const img = event.target;
        if (!(img instanceof HTMLImageElement)) return;
        const fallback = document.createElement('span');
        fallback.className = 'photo-fallback';
        fallback.textContent = img.closest('.spec-preview') ? 'Open document ↗' : 'View product details ↗';
        img.replaceWith(fallback);
      }, { capture: true, signal: this.abort.signal });
      // Local specification filters need access to values beyond the first page.
      const sidebar = this.querySelector('#sidebar');
      for (const type of ['focusin','pointerdown']) sidebar.addEventListener(type, () => this.loadIndex(), { signal: this.abort.signal });
      this.refresh();
    }
    disconnectedCallback() { this.abort?.abort(); this.querySelector('dialog')?.close(); this.initialized = false; }
    setView(view) {
      this.grid.dataset.layout = view;
      this.querySelectorAll('[data-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.view === view)));
      collectionViews.set(this.viewKey, view);
    }
    filterProfile() {
      const name = normalize(`${this.dataset.collectionTitle || ''} ${this.dataset.collectionHandle || ''}`);
      if (/vault|handhole|polymer concrete|hdpe|pedestrian/.test(name)) return 'vaults';
      if (/connector|adapter|adaptor|patch cord|patch panel|pigtail|coupler|splice tray/.test(name)) return 'connectivity';
      if (/rack|cabinet/.test(name)) return 'racks';
      if (/tool|test|splicer|cleaver|cleaning|stripper|microscope|otdr/.test(name)) return 'tools';
      if (/network|switch|media converter|transceiver/.test(name)) return 'network';
      if (/fiber|fibre/.test(name)) return 'fiber';
      if (/cat[3-8]|copper|coax|ethernet cable|category cable/.test(name)) return 'copper';
      return this.dataset.family || 'general';
    }
    readBulkOrder(card, tags, type, title) {
      const size = bulkSize(title);
      let family = bulkPhrase(type) || size.family;
      const collectionHandles = (card.dataset.bulkCollections || '').split(' ');
      const labels = tags.map(bulkPhrase);
      const titleWords = bulkPhrase(title);
      const typeWords = bulkPhrase(type);
      const positions = this.bulkNames.flatMap((department, departmentIndex) => {
        if (!labels.includes(bulkPhrase(department))) return [];
        let best = {department, departmentIndex, menuIndex: Infinity, score: 0};
        this.bulkMenu.forEach(entry => {
          if (entry.department !== department) return;
          let score = 0;
          if (collectionHandles.includes(entry.handle)) score = 100;
          if (labels.includes(entry.path)) score = Math.max(score, 90);
          if (labels.includes(entry.handleWords) || typeWords === entry.handleWords) score = Math.max(score, 80);
          for (const name of entry.names) {
            if (labels.includes(name) || typeWords === name) score = Math.max(score, 70);
            else if (containsPhrase(typeWords, name) || containsPhrase(titleWords, name)) score = Math.max(score, 40 + name.length / 100);
          }
          if (entry.groups.some(group => labels.includes(group))) score = Math.max(score, 10);
          if (score > best.score) best = {department, departmentIndex, menuIndex: entry.index, score};
        });
        return [best];
      });
      let familyRank = 2;
      if (/\bmachine bolt\b/.test(family + ' ' + titleWords)) {
        familyRank = 0;
        family = 'machine bolt';
      } else if (/\bdouble arming bolt\b/.test(family + ' ' + titleWords)) {
        familyRank = 1;
        family = 'double arming bolt';
      }
      return {positions, family, familyRank, dimensions: size.dimensions};
    }
    compareBulk(a, b) {
      const department = this.filters.Department?.[0];
      const position = item => item.bulkOrder.positions.find(entry => entry.department === department) || item.bulkOrder.positions[0] || {departmentIndex: Infinity, menuIndex: Infinity};
      const left = position(a), right = position(b);
      const order = left.departmentIndex - right.departmentIndex || left.menuIndex - right.menuIndex ||
        a.bulkOrder.familyRank - b.bulkOrder.familyRank || numeric(a.bulkOrder.family, b.bulkOrder.family);
      if (order) return order;
      const sizeA = a.bulkOrder.dimensions, sizeB = b.bulkOrder.dimensions;
      for (let i = 0; i < Math.max(sizeA.length, sizeB.length); i++) {
        const av = sizeA[i] ?? Infinity, bv = sizeB[i] ?? Infinity;
        if (av !== bv) return av < bv ? -1 : 1;
      }
      return numeric(a.title, b.title) || a.index - b.index;
    }
    readCard(card, index) {
      const title = card.dataset.title || card.querySelector('.product-title')?.textContent || '';
      const vendor = card.dataset.vendor || card.querySelector('.vendor')?.textContent || '';
      const type = card.dataset.productType || '';
      const profile = this.filterProfile();
      let tags = []; try { tags = JSON.parse(card.dataset.tags || '[]'); } catch (_) {}
      const facets = { Manufacturer: [vendor].filter(Boolean), 'Product type': [type].filter(Boolean), Availability: [card.dataset.available === 'false' ? 'Out of stock' : 'In stock'] };
      const explicitKeys = [];
      const set = (key, value, explicit = false) => {
        key = facetKey(key); if (!key) return;
        const values = facetValues(key, value); if (!values.length) return;
        if (key === 'Size' && profile === 'racks' && values.every(item => /^\d+\s*(?:u|ru)$/i.test(item))) key = 'Rack height';
        facets[key] = facetValues(key, values);
        if (explicit && !explicitKeys.includes(key)) explicitKeys.push(key);
      };
      const fallback = (key, value) => { if (!facets[key]?.length) set(key, value); };
      // Options, labelled tags, specifications, then custom metafields: later sources override earlier ones.
      const readList = selector => card.querySelectorAll(`${selector} dt`).forEach(dt => {
        const dd = dt.nextElementSibling; let value = dd?.textContent || '';
        if (dd?.dataset.values) { try { value = JSON.parse(dd.dataset.values); } catch (_) {} }
        set(dt.textContent, value, true);
      });
      readList('[data-filter-options]');
      tags.forEach(tag => { const pair = String(tag).match(/^([^:|=]+)\s*[:|=]\s*(.+)$/); if (pair) set(pair[1], pair[2], true); });
      readList('[data-comparison-specs]');
      readList('[data-filter-specs]');
      const low = title.toLowerCase();
      const tagValues = tags.map(tag => String(tag).trim());
      if (profile === 'vaults') {
        const size = title.match(/(\d+(?:\.\d+)?)\s*["″]?\s*[x×]\s*(\d+(?:\.\d+)?)\s*["″]?\s*[x×]\s*(\d+(?:\.\d+)?)/i);
        if (size) { fallback('Size', `${size[1]} × ${size[2]}`); fallback('Depth', `${size[3]} in.`); }
        fallback('Load rating', card.dataset.rating);
        if (!facets['Load rating']?.length) { const tier = title.match(/\btier\s*(15|22)\b/i); if (tier) fallback('Load rating', `Tier ${tier[1]}`); }
        if (/polymer concrete/.test(low)) fallback('Material', 'Polymer concrete');
        else if (/\bhdpe\b/.test(low)) fallback('Material', 'HDPE');
        else if (/fiberglass/.test(low)) fallback('Material', 'Fiberglass');
      }
      if (profile === 'fiber' || profile === 'connectivity') {
        const mode = title.match(/\bom[1-5]\b|\bos[12]\b|single[ -]?mode|multi[ -]?mode/i);
        fallback('Fiber mode', mode?.[0] || tagValues.find(tag => /^(?:om[1-5]|os[12]|single[ -]?mode|multi[ -]?mode)$/i.test(tag)));
        const count = title.match(/\b(\d+)\s*[- ]?(?:fiber|strand|fibre)\b/i);
        fallback('Fiber count', count?.[1] || tagValues.find(tag => /^\d+\s*(?:fiber|fibre|strand)s?$/i.test(tag)));
        const jacket = title.match(/\b(plenum|riser|lszh)\b/i);
        fallback('Jacket rating', jacket?.[0] || tagValues.find(tag => /^(?:plenum|riser|lszh)$/i.test(tag)));
        if (/non[ -]?armou?red|unarmou?red/.test(low)) fallback('Construction', 'Non-armored');
        else if (/micro[ -]?armou?r/.test(low)) fallback('Construction', 'Micro-armored');
        else if (/armou?r/.test(low)) fallback('Construction', 'Armored');
        else fallback('Construction', tagValues.find(tag => /^(?:non[ -]?armou?red|unarmou?red|armou?red|micro[ -]?armou?red)$/i.test(tag)));
        const connectors = [...title.matchAll(/\b(LC|SC|ST|FC|MPO|MTP)\b/gi)].map(match => match[1].toUpperCase());
        fallback('Connector type', connectors);
        const polish = [...title.matchAll(/\b(APC|UPC|PC)\b/gi)].map(match => match[1].toUpperCase());
        fallback('Polish', polish);
      }
      if (['fiber', 'connectivity', 'copper'].includes(profile)) {
        if (/indoor\s*[\/&-]\s*outdoor|indoor outdoor/.test(low)) fallback('Application', 'Indoor/Outdoor');
        else if (/direct[ -]?burial/.test(low)) fallback('Application', 'Direct burial');
        else if (/outdoor/.test(low)) fallback('Application', 'Outdoor');
        else if (/indoor/.test(low)) fallback('Application', 'Indoor');
        fallback('Application', tagValues.find(tag => /^(?:indoor|outdoor|indoor\s*\/\s*outdoor|direct[ -]?burial)$/i.test(tag)));
      }
      if (profile === 'copper') {
        const jacket = title.match(/\b(CMXF|CMXT|CMX|CMP|CMR|LSZH)\b/i); fallback('Jacket', jacket?.[0]);
        const gauge = title.match(/\b(\d+)\s*[- ]?AWG\b/i); if (gauge) fallback('Gauge', `${gauge[1]} AWG`);
        const category = title.match(/\bcat(?:egory)?\s*([3-8](?:a|e)?)\b/i); if (category) fallback('Category', `CAT${category[1].toUpperCase()}`);
        if (/unshielded|\butp\b/.test(low)) fallback('Shielding', 'Unshielded');
        else if (/shielded|\bstp\b|\bftp\b/.test(low)) fallback('Shielding', 'Shielded');
      }
      if (profile === 'racks') {
        fallback('Rack height', [...title.matchAll(/\b(\d+)\s*U\b/gi)].map(match => `${match[1]}U`));
        if (/hinged/.test(low)) fallback('Style', 'Hinged');
        else if (/open[ -]?frame/.test(low)) fallback('Style', 'Open frame');
        else if (/enclosed/.test(low)) fallback('Style', 'Enclosed');
        if (/wall[ -]?mount/.test(low)) fallback('Mounting', 'Wall mount');
        else if (/floor[ -]?mount|floor[ -]?standing/.test(low)) fallback('Mounting', 'Floor standing');
      }
      if (profile === 'tools') {
        const types = [[/fusion splicer/i, 'Fusion splicers'], [/\botdr\b/i, 'OTDRs'], [/microscope|inspection scope/i, 'Inspection tools'], [/launch cable/i, 'Launch cables'], [/cleaver/i, 'Cleavers'], [/stripper/i, 'Strippers'], [/tool kit|splicer[’']?s kit/i, 'Tool kits'], [/cleaner|cleaning kit/i, 'Cleaning tools']];
        fallback('Tool type', types.find(([pattern]) => pattern.test(title))?.[1]);
      }
      if (this.bulkCategories) {
        const labels = new Set(tags.map(normalize));
        facets.Department = this.bulkNames.filter(name => labels.has(normalize(name)));
      }
      const search = normalize([card.dataset.search || '', title, ...tags, ...Object.values(facets).flat()].join(' '));
      const bulkOrder = this.bulkCategories ? this.readBulkOrder(card, tags, type, title) : null;
      return { card, title, vendor, facets, bulkOrder, tags: tags.map(normalize), explicitKeys, index, price: Number(card.dataset.price || 0), search };
    }
    addRecords(cards, page) {
      const known = new Set(this.records.map(item => item.card.dataset.product));
      cards.forEach((card, i) => {
        if (!known.has(card.dataset.product)) {
          this.records.push(this.readCard(card, (page - 1) * this.pageSize + i));
          known.add(card.dataset.product);
        }
      });
      this.records.sort((a,b) => a.index - b.index);
    }
    values(item, key) { const value = item.facets[key]; return Array.isArray(value) ? value : value ? [value] : []; }
    matches(item, skip) {
      const terms = normalize(this.search.value).split(/\s+/).filter(Boolean);
      const dimension = this.search.value.match(/\d+(?:\.\d+)?\s*[x×]\s*\d+(?:\.\d+)?(?:\s*[x×]\s*\d+(?:\.\d+)?)?/i);
      const sizeMatches = !dimension || normalize(item.title).includes(normalize(dimension[0]));
      const bulkMatches = !this.bulkCategories || item.tags.includes('bulk savings');
      return bulkMatches && sizeMatches && terms.every(term => item.search.includes(term)) && Object.entries(this.filters).every(([key, chosen]) => key === skip || !chosen.length || chosen.some(value => this.values(item, key).includes(value)));
    }
    filterKeys() {
      const groups = {
        vaults: ['Size','Depth','Manufacturer','Load rating','Material','Lid type'],
        fiber: ['Fiber mode','Fiber count','Application','Jacket rating','Construction','Cable type','Manufacturer'],
        connectivity: ['Fiber mode','Connector type','Connector A','Connector B','Polish','Fiber count','Application','Manufacturer'],
        copper: ['Application','Jacket','Category','Gauge','Shielding','Manufacturer'],
        racks: ['Rack height','Style','Mounting','Depth','Width','Manufacturer'],
        tools: ['Tool type','Application','Manufacturer','Product type'],
        network: ['Port count','Speed','PoE','Connector type','Manufacturer','Product type'],
        general: ['Product type','Manufacturer']
      };
      const explicit = this.records.flatMap(item => item.explicitKeys);
      const nativeKeys = new Set(this.nativeFilters.map(filter => facetKey(filter.label) || facetKey(String(filter.id || '').split('.').pop())));
      return [...new Set([...(groups[this.filterProfile()] || groups.general), ...explicit, 'Availability'])].filter(key => !nativeKeys.has(key) && this.records.some(item => this.values(item, key).length));
    }
    hasNativeSelection() {
      return this.nativeFilters.some(filter => filter.values.some(value => value.active) || filter.type === 'price_range' && (filter.min?.value != null || filter.max?.value != null));
    }
    recommendedURL(url) {
      // Each collection can use manual, best-selling, newest, or another saved Shopify order.
      // Removing a conflicting sort_by restores that exact default, rather than forcing manual.
      if (this.dataset.defaultSort && url.searchParams.has('sort_by') && url.searchParams.get('sort_by') !== this.dataset.defaultSort) url.searchParams.delete('sort_by');
      (this.excludedNativeFilters || []).forEach(filter => url.searchParams.delete(filter.id));
      return url;
    }
    withLocalState(url) {
      this.recommendedURL(url);
      if (this.bulkCategories) url.searchParams.set('view', 'bulk-savings');
      const set = (key, value) => { if (value) url.searchParams.set(key, value); else url.searchParams.delete(key); };
      set('tsc_q', this.search.value.trim());
      set('tsc_sort', this.querySelector('[data-sort]').value === 'featured' ? '' : this.querySelector('[data-sort]').value);
      set('tsc_view', this.grid.dataset.layout === 'list' ? 'list' : '');
      const filters = Object.fromEntries(Object.entries(this.filters).filter(([,values]) => values.length));
      set('tsc_filters', Object.keys(filters).length ? JSON.stringify(filters) : '');
      return url;
    }
    writeLocalState() {
      const url = this.withLocalState(new URL(location.href));
      const old = new URL(location.href);
      if (['tsc_q','tsc_sort','tsc_filters'].some(key => old.searchParams.get(key) !== url.searchParams.get(key))) this.page = 1;
      if (this.page > 1) url.searchParams.set(this.param, this.page); else url.searchParams.delete(this.param);
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      this.refresh();
    }
    goNative(href) {
      if (!href) return;
      const url = new URL(href, location.href), current = new URL(location.href);
      // Reset and Shopify filter links must retain the Bulk Savings tag scope.
      if (this.bulkCategories) {
        url.pathname = location.pathname;
        url.searchParams.set('view', 'bulk-savings');
      }
      if (url.origin !== location.origin || url.pathname !== location.pathname) return;
      url.searchParams.delete('page'); url.searchParams.delete('section_id');
      // Keep the same draft theme; all server-rendered pages use the saved collection order.
      ['preview_theme_id','sort_by'].forEach(key => { if (current.searchParams.has(key) && !url.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key)); });
      this.withLocalState(url);
      location.assign(url.href);
    }
    orderProductTypes(values) {
      if (!this.bulkCategories) return values;
      const products = [...this.records].sort((a, b) => this.compareBulk(a, b));
      const names = ['Machine Bolts', 'Double Arming Bolts', ...products.flatMap(item => this.values(item, 'Product type'))];
      const ranks = new Map([...new Set(names.map(bulkPhrase))].map((name, index) => [name, index]));
      const rank = value => ranks.get(bulkPhrase(typeof value === 'string' ? value : value.value || value.label)) ?? Infinity;
      return [...values].sort((a, b) => rank(a) - rank(b));
    }
    renderNativeFilter(filter) {
      if (filter.type === 'price_range') {
        const value = cents => cents == null ? '' : cents / 100;
        return `<fieldset class="filter-block"><legend>${esc(filter.label)}</legend><form class="tsc-price-filter" data-native-price><label>Min<input type="number" step="0.01" min="0" name="${esc(filter.min.name)}" value="${esc(value(filter.min.value))}" aria-label="Minimum ${esc(filter.label)}"></label><label>Max<input type="number" step="0.01" min="0" name="${esc(filter.max.name)}" value="${esc(value(filter.max.value))}" placeholder="${esc(value(filter.rangeMax))}" aria-label="Maximum ${esc(filter.label)}"></label><button class="btn" type="submit">Apply</button></form></fieldset>`;
      }
      const size = facetKey(filter.label) === 'Size';
      const localActive = Boolean(this.search.value || Object.values(this.filters).some(values => values.length));
      const values = filter.id === 'filter.p.product_type' || facetKey(filter.label) === 'Product type' ? this.orderProductTypes(filter.values) : filter.values;
      return `<fieldset class="filter-block"><legend>${esc(filter.label)}${size ? '<span class="filter-help">Length × width, in inches</span>' : ''}</legend><div class="filter-options ${size ? 'size-options' : ''}">${values.map(value => `<label class="filter-option ${size ? 'size-choice' : ''}"><input type="checkbox" data-native-filter="${esc(filter.id)}" data-add-url="${esc(value.addURL || '')}" data-remove-url="${esc(value.removeURL || '')}" value="${esc(value.value)}" ${value.active ? 'checked' : ''} ${!value.count && !value.active ? 'disabled' : ''}><span>${esc(value.label)}</span><span class="option-count">${localActive ? '' : value.count}</span></label>`).join('')}</div></fieldset>`;
    }
    renderFilters() {
      if (this.bulkCategories) {
        const category = this.filters.Department?.[0] || '';
        this.bulkCategories.querySelectorAll('[data-bulk-category]').forEach(button => {
          button.setAttribute('aria-pressed', String(button.dataset.bulkCategory === category));
        });
        this.querySelector('[data-bulk-current]').textContent = category;
        this.querySelector('[data-bulk-current]').hidden = !category;
        this.querySelector('[data-bulk-divider]').hidden = !category;
        const home = this.querySelector('[data-bulk-home]');
        if (category) home.removeAttribute('aria-current');
        else home.setAttribute('aria-current', 'page');
      }
      const focus = document.activeElement?.closest('[data-facet]');
      const focusKey = focus?.dataset.facet, focusValue = focus?.value;
      const localMarkup = this.filterKeys().map(key => {
        // Bulk product types follow the listing groups; other facets retain their source order.
        let values = [...new Set(this.records.flatMap(item => this.values(item, key)))];
        if (key === 'Product type') values = this.orderProductTypes(values);
        const base = this.records.filter(item => this.matches(item, key));
        return `<fieldset class="filter-block"><legend>${esc(key)}${key === 'Size' ? '<span class="filter-help">Length × width, in inches</span>' : ''}</legend><div class="filter-options ${key === 'Size' ? 'size-options' : ''}">${values.map(value => {
          const count = base.filter(item => this.values(item, key).includes(value)).length;
          const checked = (this.filters[key] || []).includes(value);
          return `<label class="filter-option ${key === 'Size' ? 'size-choice' : ''}"><input type="checkbox" data-facet="${esc(key)}" value="${esc(value)}" ${checked ? 'checked' : ''} ${!count && !checked ? 'disabled' : ''}><span>${esc(value)}</span><span class="option-count">${count}</span></label>`;
        }).join('')}</div></fieldset>`;
      }).join('');
      const nativeMarkup = this.nativeFilters.map(filter => this.renderNativeFilter(filter)).join('');
      const configuredSpecifications = this.nativeFilters.some(filter => !['Availability',''].includes(facetKey(filter.label)) && filter.type !== 'price_range' || filter.type !== 'price_range' && !['filter.v.availability','filter.p.vendor','filter.p.product_type'].includes(filter.id) && !facetKey(filter.label));
      this.querySelector('[data-filters]').innerHTML = configuredSpecifications ? nativeMarkup + localMarkup : localMarkup + nativeMarkup;
      if (focusKey) [...this.querySelectorAll('[data-facet]')].find(input => input.dataset.facet === focusKey && input.value === focusValue)?.focus({ preventScroll: true });
      const nativeCount = this.nativeFilters.reduce((count, filter) => count + filter.values.filter(value => value.active).length + (filter.type === 'price_range' && (filter.min?.value != null || filter.max?.value != null) ? 1 : 0), 0);
      const count = Object.values(this.filters).reduce((sum, values) => sum + values.length, nativeCount);
      this.querySelector('[data-filter-count]').textContent = count ? `(${count})` : '';
      this.querySelector('#sidebar [data-reset]').hidden = !count && !this.search.value;
      this.querySelector('[data-clear-search]').hidden = !this.search.value;
      const active = this.querySelector('[data-active-filters]');
      const nativeChips = this.nativeFilters.map(filter => filter.type === 'price_range' ? filter.min?.value != null || filter.max?.value != null ? `<button class="filter-chip" type="button" data-native-url="${esc(filter.removeURL)}" aria-label="Remove ${esc(filter.label)}">${esc(filter.label)} <b aria-hidden="true">×</b></button>` : '' : filter.values.filter(value => value.active).map(value => `<button class="filter-chip" type="button" data-native-url="${esc(value.removeURL)}" aria-label="Remove ${esc(filter.label + ': ' + value.label)}">${esc(value.label)} <b aria-hidden="true">×</b></button>`).join('')).join('');
      active.innerHTML = nativeChips + Object.entries(this.filters).flatMap(([key, values]) => values.map(value => `<button class="filter-chip" type="button" data-remove-key="${esc(key)}" data-remove-value="${esc(value)}" aria-label="Remove ${esc(key + ': ' + value)}">${esc(value)} <b aria-hidden="true">×</b></button>`)).join('') + (this.search.value ? `<button class="filter-chip" type="button" data-clear-search>Search: ${esc(this.search.value)} <b aria-hidden="true">×</b></button>` : '');
      active.hidden = !active.children.length;
    }
    localOrder() {
      return Boolean(this.bulkCategories || this.search.value.trim() || this.querySelector('[data-sort]').value !== 'featured' ||
        Object.values(this.filters).some(values => values.length));
    }
    pageURL(page) {
      const url = this.withLocalState(new URL(location.href));
      if (page > 1) url.searchParams.set(this.param, page); else url.searchParams.delete(this.param);
      return url;
    }
    movePage(page) {
      if (!Number.isInteger(page) || page < 1) return;
      this.page = page;
      this.writeLocalState();
      this.querySelector('.results').focus({ preventScroll: true });
      this.querySelector('.results').scrollIntoView({ block: 'start' });
    }
    renderPagination(last) {
      this.querySelector('[data-load]').hidden = last <= 1 && !this.failed;
      this.querySelector('[data-pagination-row]').hidden = last <= 1;
      const start = Math.max(2, Math.min(this.page - 1, last - 3));
      const end = Math.min(last - 1, Math.max(this.page + 1, 4));
      const pages = last <= 7 ? Array.from({length: last}, (_, i) => i + 1) :
        [1, ...Array.from({length: end - start + 1}, (_, i) => start + i), last];
      const markup = [];
      pages.forEach((page, index) => {
        if (index && page > pages[index - 1] + 1) markup.push('<span class="ts-page-ellipsis" aria-hidden="true">…</span>');
        if (page === this.page) {
          markup.push(`<span class="ts-page-link ts-page-number" aria-current="page" aria-label="Page ${page}">${page}</span>`);
        } else {
          markup.push(`<a class="ts-page-link ts-page-number" data-page-number="${page}" href="${esc(this.pageURL(page).href)}" aria-label="Page ${page}">${page}</a>`);
        }
      });
      const numbers = this.querySelector('[data-page-numbers]');
      const html = markup.join('');
      if (numbers.innerHTML !== html) numbers.innerHTML = html;
      this.querySelectorAll('[data-page-step]').forEach(link => {
        const target = this.page + Number(link.dataset.pageStep);
        const disabled = target < 1 || target > last;
        if (disabled) {
          link.removeAttribute('href');
          link.setAttribute('aria-disabled', 'true');
          link.setAttribute('tabindex', '-1');
        } else {
          link.href = this.pageURL(target);
          link.removeAttribute('aria-disabled');
          link.removeAttribute('tabindex');
        }
      });
    }
    applySearch() {
      const local = this.localOrder();
      const sort = this.querySelector('[data-sort]').value;
      const matches = this.records.filter(item => this.matches(item));
      if (local) matches.sort((a, b) => {
        if (sort === 'price-asc') return a.price - b.price || numeric(a.title, b.title);
        if (sort === 'price-desc') return b.price - a.price || numeric(a.title, b.title);
        if (sort === 'name') return numeric(a.title, b.title);
        if (this.bulkCategories) return this.compareBulk(a, b);
        return a.index - b.index;
      });
      const complete = this.loaded.size >= this.pages;
      const total = local ? matches.length : Number(this.dataset.total);
      const last = local ? Math.max(1, Math.ceil(total / this.pageSize)) : this.pages;
      if (complete && this.page > last) {
        this.page = last;
        history.replaceState(history.state, '', this.pageURL(last));
      }
      const start = (this.page - 1) * this.pageSize;
      const visible = local ? matches.slice(start, start + this.pageSize) :
        matches.filter(item => item.index >= start && item.index < start + this.pageSize);
      if (local || this.loaded.has(this.page)) {
        const nodes = visible.map(item => item.card);
        if (nodes.length !== this.grid.children.length || nodes.some((node, i) => this.grid.children[i] !== node)) this.grid.replaceChildren(...nodes);
      }
      this.querySelector('[data-empty]').hidden = total > 0 || (local && !complete);
      this.status.textContent = total + (total === 1 ? ' product' : ' products') + (local && !complete ? ' found so far' : '');
      const summary = this.querySelector('[data-summary]');
      summary.textContent = this.failed ? 'Some products could not be loaded.' : this.pending.size ? 'Loading products…' :
        visible.length ? 'Showing ' + (start + 1) + '–' + (start + visible.length) + ' of ' + total + ' products' : '';
      this.querySelector('[data-retry]').hidden = !this.failed;
      this.renderPagination(last);
      this.renderFilters();
      this.updateCompare();
    }
    refresh() {
      this.applySearch();
      if (this.localOrder() || this.indexWanted) this.loadIndex();
      else if (!this.loaded.has(this.page)) this.loadPage(this.page).then(() => this.applySearch());
    }
    sectionURL(page) {
      const url = this.pageURL(page);
      url.searchParams.set('section_id', this.dataset.sectionId);
      return url.href;
    }
    async sectionCards(url, page, priority) {
      const response = await fetch(url, {signal: this.abort.signal, credentials: 'same-origin', priority});
      if (!response.ok) throw new Error('Collection unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = doc.querySelector('ts-collection-results');
      if (Number(source?.dataset.page) !== page) throw new Error('Unexpected collection page');
      const cards = source.querySelectorAll('#product-grid > .product-card');
      if (!cards.length) throw new Error('Missing collection cards');
      return cards;
    }
    preparePage(link) {
      if (!link.matches('a[data-page-step], a[data-page-number]') || link.getAttribute('aria-disabled') === 'true' || this.localOrder()) return;
      const page = link.hasAttribute('data-page-number') ? Number(link.dataset.pageNumber) : this.page + Number(link.dataset.pageStep);
      if (!Number.isInteger(page) || page < 1 || page > this.pages || this.loaded.has(page) || this.pending.has(page)) return;
      const url = this.sectionURL(page);
      if (this.preparedPage?.url === url && this.preparedPage.expires > Date.now()) return;
      document.dispatchEvent(new CustomEvent('ts:navigation-priority', {detail: {url: link.href, handled: true}}));
      // This component's normal click handler can reuse an in-flight or finished response.
      this.preparedPage = {url, expires: Date.now() + 15000, cards: this.sectionCards(url, page, 'low').catch(() => null)};
    }
    async loadPage(page) {
      if (this.loaded.has(page)) return true;
      if (this.pending.has(page)) return this.pending.get(page);
      const signal = this.abort.signal;
      const request = (async () => {
        try {
          const url = this.sectionURL(page);
          const prepared = this.preparedPage;
          const reusable = prepared?.url === url && prepared.expires > Date.now();
          if (reusable) this.preparedPage = null;
          const cards = (reusable && await prepared.cards) || await this.sectionCards(url, page, 'auto');
          if (signal.aborted) return false;
          this.addRecords(cards, page);
          this.loaded.add(page);
          this.failed = false;
          return true;
        } catch (_) {
          if (!signal.aborted) this.failed = true;
          return false;
        } finally {
          if (!signal.aborted) this.pending.delete(page);
        }
      })();
      this.pending.set(page, request);
      this.querySelector('[data-summary]').textContent = 'Loading products…';
      return request;
    }
    async loadIndex() {
      if (this.indexing || this.loaded.size >= this.pages) return;
      this.indexWanted = true;
      this.indexing = true;
      const signal = this.abort.signal;
      try {
        for (let page = 1; page <= this.pages && !signal.aborted; page++) {
          if (!this.loaded.has(page) && !await this.loadPage(page)) break;
        }
      } finally {
        if (!signal.aborted) {
          this.indexing = false;
          this.applySearch();
        }
      }
    }
    toggleFilters(open) {
      this.querySelector('.catalog-layout').classList.toggle('filters-open', open);
      this.querySelector('[data-filter-toggle]').setAttribute('aria-expanded', String(open));
      if (!open) { this.querySelector('[data-filter-toggle]').focus({ preventScroll: true }); this.querySelector('.results').scrollIntoView({ block: 'start', behavior: 'instant' }); }
    }
    updateCompare() {
      this.querySelector('[data-compare-bar]').hidden = this.selected.size === 0;
      this.querySelector('[data-compare-count]').textContent = `${this.selected.size} selected`;
      this.querySelector('[data-open-compare]').disabled = this.selected.size < 2;
      this.querySelectorAll('[data-compare]').forEach(box => {
        box.checked = this.selected.has(box.dataset.compare);
        box.disabled = this.selected.size >= 3 && !box.checked;
        box.closest('.product-card').dataset.selected = String(box.checked);
      });
    }
    openComparison() {
      const cards = this.records.map(item => item.card).filter(card => this.selected.has(card.dataset.product));
      if (cards.length < 2) return;
      const make = (tag, text) => { const node = document.createElement(tag); if (text !== undefined) node.textContent = text; return node; };
      const specs = cards.map(card => new Map([...card.querySelectorAll('[data-comparison-specs] dt')].map(dt => [dt.textContent, dt.nextElementSibling?.textContent || 'See product page'])));
      const keys = [...new Set(specs.flatMap(spec => [...spec.keys()]))].filter(key => key !== 'Product');
      const table = make('table'), head = make('thead'), titleRow = make('tr');
      titleRow.append(make('th', 'Product'));
      cards.forEach((card, i) => {
        const cell = make('th'); cell.scope = 'col';
        const image = card.querySelector('.product-photo img'); if (image) cell.append(image.cloneNode(true));
        cell.append(make('h3', specs[i].get('Product') || card.querySelector('.product-title').textContent));
        cell.append(card.querySelector('.product-action').cloneNode(true)); titleRow.append(cell);
      });
      head.append(titleRow); table.append(head);
      const body = make('tbody');
      keys.forEach(key => { const row = make('tr'), label = make('th', key); label.scope = 'row'; row.append(label); specs.forEach(spec => row.append(make('td', spec.get(key) || 'See product page'))); body.append(row); });
      const docs = make('tr'); docs.append(make('th', 'Documentation'));
      cards.forEach(card => { const cell = make('td'), source = card.querySelector('.spec-link'); if (source) { const link = make('a', 'Open spec sheet ↗'); link.href = source.href; link.target = '_blank'; link.rel = 'noopener'; cell.append(link); } else cell.textContent = 'See product page'; docs.append(cell); });
      body.append(docs); table.append(body); this.querySelector('[data-comparison]').replaceChildren(table);
      this.querySelector('dialog').showModal(); this.querySelector('[data-close-compare]').focus();
    }
  }
  customElements.define('ts-collection-results', CollectionResults);
})();
